// ✅ src/pages/VehicleV2/VehicleTransferEntryListing.jsx  (FULL UPDATED FILE)

import React, {
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  FaSearch,
  FaFilter,
  FaEye,
  FaSyncAlt,
  FaUndo,
  FaPlus,
  FaArrowLeft,
  FaExchangeAlt,
} from "react-icons/fa";

const API_BASE = "http://nissi-india.com/api/vehicleTransferEntryListing";

/* ---------------- helpers ---------------- */
const toInt = (v, def = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : def;
};

const clean = (v) => String(v ?? "").trim();

const normalizeSelect = (v, fallback = "Select") => {
  const s = clean(v);
  return s || fallback;
};

const defaultFilters = {
  filterType: "own", // own / spares
  selectedBranchId: "Select",
  storeId: "Select",
  vehicleTypeIndex: "Select",
  vehicleCode: "",
  vehicleName: "",
  vehicleRegNo: "",
};

const VehicleTransferEntry = () => {
  const navigate = useNavigate();

  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  /* ---------------- shared local/session storage pattern ---------------- */
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId =
    localStorage.getItem("branchId") ||
    localStorage.getItem("Branchid") ||
    localStorage.getItem("branchid") ||
    "8";
  const branchType =
    localStorage.getItem("branchType") ||
    localStorage.getItem("branchtype") ||
    "HO";
  const userId =
    localStorage.getItem("userId") ||
    localStorage.getItem("loginuserid") ||
    "0";
  const loginUserId = sessionStorage.getItem("loginUserId") || userId || "0";

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branchType, userId]
  );

  /* ---------------- state ---------------- */
  const [pageLoading, setPageLoading] = useState(false);
  const [dropdownLoading, setDropdownLoading] = useState(false);
  const [gridLoading, setGridLoading] = useState(false);

  const [showFilters, setShowFilters] = useState(true);

  const [branches, setBranches] = useState([]);
  const [stores, setStores] = useState([]);
  const [vehicleTypes, setVehicleTypes] = useState([]);

  const [filters, setFilters] = useState(defaultFilters);
  const [rows, setRows] = useState([]);

  const isBusy = pageLoading || dropdownLoading || gridLoading;

  /* ---------------- styles ---------------- */
  const cardClass = useMemo(
    () =>
      `p-6 mt-6 rounded-2xl shadow-xl border ${
        isDark
          ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
          : `bg-white ${currentSkin.glass} text-black`
      }`,
    [isDark, currentSkin]
  );

  const sectionClass = useMemo(
    () =>
      `rounded-xl p-6 border ${
        isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"
      }`,
    [isDark]
  );

  const inputClass = useMemo(
    () =>
      `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
        isDark
          ? "bg-gray-700/30 border-gray-600 text-white placeholder:text-gray-400"
          : "bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
      }`,
    [isDark]
  );

  const labelClass = useMemo(
    () =>
      `block text-sm font-medium mb-2 ${
        isDark ? "text-gray-300" : "text-gray-700"
      }`,
    [isDark]
  );

  const pillClass = useMemo(
    () =>
      `inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
        isDark ? "bg-blue-900/60 text-blue-100" : "bg-blue-100 text-blue-800"
      }`,
    [isDark]
  );

  /* ---------------- helpers ---------------- */
  const canUse = useCallback((cond, msg) => {
    if (!cond) toast.error(msg);
    return !!cond;
  }, []);

  const buildDropdownParams = useCallback(
    (selectedBranchIdValue) => ({
      branchId,
      branchType,
      loginUserId,
      selectedBranchId:
        selectedBranchIdValue ?? normalizeSelect(filters.selectedBranchId),
    }),
    [branchId, branchType, loginUserId, filters.selectedBranchId]
  );

  const buildListParams = useCallback(
    (activeFilters) => ({
      filterType: activeFilters.filterType,
      branchId,
      branchType,
      loginUserId,
      selectedBranchId: normalizeSelect(activeFilters.selectedBranchId),
      storeId: normalizeSelect(activeFilters.storeId),
      vehicleTypeIndex: normalizeSelect(activeFilters.vehicleTypeIndex),
      vehicleCode: clean(activeFilters.vehicleCode),
      vehicleName: clean(activeFilters.vehicleName),
      vehicleRegNo: clean(activeFilters.vehicleRegNo),
    }),
    [branchId, branchType, loginUserId]
  );

  /* =========================================================
     API LOADERS
  ========================================================= */
  const loadDropdowns = useCallback(
    async (selectedBranchIdValue) => {
      try {
        setDropdownLoading(true);

        const { data } = await axios.get(`${API_BASE}/dropdowns`, {
          params: buildDropdownParams(selectedBranchIdValue),
          headers: commonHeaders,
        });

        if (!data?.success) {
          throw new Error(data?.message || "Failed to load dropdowns");
        }

        setBranches(Array.isArray(data.branches) ? data.branches : []);
        setStores(Array.isArray(data.stores) ? data.stores : []);
        setVehicleTypes(Array.isArray(data.vehicleTypes) ? data.vehicleTypes : []);
      } catch (err) {
        console.error(err);
        toast.error(
          err?.response?.data?.message ||
            err.message ||
            "Failed to load dropdowns"
        );
      } finally {
        setDropdownLoading(false);
      }
    },
    [buildDropdownParams, commonHeaders]
  );

  const loadList = useCallback(
    async ({ silent = false, activeFilters = defaultFilters } = {}) => {
      if (!canUse(toInt(loginUserId), "loginUserId missing")) return;
      if (!canUse(toInt(branchId), "branchId missing")) return;

      try {
        setGridLoading(true);

        const { data } = await axios.get(`${API_BASE}/list`, {
          params: buildListParams(activeFilters),
          headers: commonHeaders,
        });

        if (!data?.success) {
          setRows([]);
          if (!silent) toast.warn(data?.message || "No data found");
          return;
        }

        const list = Array.isArray(data.list) ? data.list : [];
        setRows(list);

        if (!silent) {
          toast.success(`Found ${data.count ?? list.length ?? 0} record(s)`);
        }
      } catch (err) {
        console.error(err);
        setRows([]);
        toast.error(
          err?.response?.data?.message || err.message || "Failed to load list"
        );
      } finally {
        setGridLoading(false);
      }
    },
    [loginUserId, branchId, buildListParams, commonHeaders, canUse]
  );

  const loadInit = useCallback(async () => {
    if (!canUse(toInt(loginUserId), "loginUserId missing")) return;
    if (!canUse(toInt(branchId), "branchId missing")) return;

    try {
      setPageLoading(true);
      await loadDropdowns(defaultFilters.selectedBranchId);
      await loadList({ silent: true, activeFilters: defaultFilters });
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.message || err.message || "Initialization failed"
      );
    } finally {
      setPageLoading(false);
    }
  }, [loginUserId, branchId, canUse, loadDropdowns, loadList]);

  /* ---------------- handlers ---------------- */
  const onFilterChange = useCallback(
    async (e) => {
      const { name, value, type } = e.target;

      setFilters((prev) => ({
        ...prev,
        [name]: type === "radio" ? value : value,
      }));

      if (name === "selectedBranchId") {
        await loadDropdowns(value);
        setFilters((prev) => ({
          ...prev,
          selectedBranchId: value,
          storeId: "Select",
        }));
      }

      if (name === "filterType" && value === "spares") {
        setFilters((prev) => ({
          ...prev,
          filterType: value,
          vehicleName: "",
          vehicleRegNo: "",
        }));
      }
    },
    [loadDropdowns]
  );

  const handleSearch = useCallback(async () => {
    await loadList({ silent: false, activeFilters: filters });
  }, [loadList, filters]);

  const handleViewAll = useCallback(async () => {
    const reset = {
      ...defaultFilters,
      filterType: filters.filterType,
    };
    setFilters(reset);
    await loadDropdowns(reset.selectedBranchId);
    await loadList({ silent: false, activeFilters: reset });
  }, [filters.filterType, loadDropdowns, loadList]);

  const handleRefresh = useCallback(async () => {
    await loadInit();
  }, [loadInit]);

  const handleClearFilters = useCallback(() => {
    setFilters((prev) => ({
      ...prev,
      selectedBranchId: "Select",
      storeId: "Select",
      vehicleTypeIndex: "Select",
      vehicleCode: "",
      vehicleName: "",
      vehicleRegNo: "",
    }));
    toast.info("Filters cleared");
  }, []);

  const handleAddNew = useCallback(() => {
    navigate("/vehicle/vehicletransfer/entry");
  }, [navigate]);

  const handleBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const handleChangeBranch = useCallback(
    async (row) => {
      try {
        const vehicleId = row?.id || row?.vehicleId || row?.raw?.fld_vehicleid;

        if (!vehicleId) {
          toast.error("VehicleId not found in selected row");
          return;
        }

        const { data } = await axios.get(`${API_BASE}/precheck-transfer`, {
          params: { vehicleId },
          headers: commonHeaders,
        });

        if (data?.success && data?.canTransfer === false) {
          toast.warning(data?.reason || "Transfer not allowed");
          return;
        }

        navigate("/vehicle/vehicletransfer", {
          state: {
            vehicleId,
            row,
            filterType: filters.filterType,
          },
        });
      } catch (err) {
        console.error(err);
        toast.error(
          err?.response?.data?.message || err.message || "Precheck failed"
        );
      }
    },
    [navigate, filters.filterType, commonHeaders]
  );

  /* ---------------- effects ---------------- */
  useEffect(() => {
    loadInit();
  }, [loadInit]);

  const recordsCount = useMemo(() => rows.length, [rows]);

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">VEHICLE TRANSFER</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div className={cardClass}>
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
            <div>
              <div className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                Vehicle Transfer Entry Listing
              </div>

              <div className={`text-sm mt-1 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                Branch ID: {branchId || "-"} {" • "} User ID: {loginUserId || "-"}
              </div>

              <div className="mt-2 flex flex-wrap gap-2">
                <span className={pillClass}>Records: {recordsCount}</span>
                <span className={pillClass}>
                  Vehicle Mode: {filters.filterType === "own" ? "Own Vehicle" : "Spares & Catalogue"}
                </span>
                <span className={pillClass}>Branch Type: {branchType || "-"}</span>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setShowFilters((s) => !s)}
                disabled={isBusy}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white"
                    : "bg-gray-200 hover:bg-gray-300 text-gray-700"
                } ${isBusy ? "opacity-70 cursor-not-allowed" : ""}`}
              >
                <FaFilter className="w-4 h-4" />
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>

              <button
                onClick={handleRefresh}
                disabled={isBusy}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white"
                    : "bg-gray-200 hover:bg-gray-300 text-gray-700"
                } ${isBusy ? "opacity-70 cursor-not-allowed" : ""}`}
              >
                <FaSyncAlt className="w-4 h-4" />
                Refresh
              </button>

              {/* <button
                onClick={handleAddNew}
                disabled={isBusy}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                  isDark ? "bg-blue-700 hover:bg-blue-600 text-white" : "bg-blue-600 hover:bg-blue-700 text-white"
                } ${isBusy ? "opacity-70 cursor-not-allowed" : ""}`}
              >
                <FaPlus />
                Add New
              </button> */}
            </div>
          </div>

          {/* Vehicle Type / Filter Section */}
          <div className={sectionClass}>
            <div className="mb-6">
              <label className={labelClass}>Vehicle Type</label>
              <div className="flex flex-wrap gap-6">
                <label className="flex items-center space-x-2">
                  <input
                    type="radio"
                    name="filterType"
                    value="own"
                    checked={filters.filterType === "own"}
                    onChange={onFilterChange}
                    className="form-radio text-blue-600"
                    disabled={isBusy}
                  />
                  <span className={isDark ? "text-gray-300" : "text-gray-700"}>
                    Own Vehicle
                  </span>
                </label>

                <label className="flex items-center space-x-2">
                  <input
                    type="radio"
                    name="filterType"
                    value="spares"
                    checked={filters.filterType === "spares"}
                    onChange={onFilterChange}
                    className="form-radio text-blue-600"
                    disabled={isBusy}
                  />
                  <span className={isDark ? "text-gray-300" : "text-gray-700"}>
                    Spares & Catalogue
                  </span>
                </label>
              </div>
            </div>

            {showFilters && (
              <div className={sectionClass}>
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <FaFilter /> FILTRATION AREA
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {/* Branch */}
                  <div>
                    <label className={labelClass}>Branch</label>
                    <select
                      name="selectedBranchId"
                      value={filters.selectedBranchId}
                      onChange={onFilterChange}
                      className={inputClass}
                      disabled={branchType !== "HO" || isBusy}
                      title={branchType !== "HO" ? "Only for HO users" : ""}
                    >
                      <option value="Select">Select</option>
                      {branches.map((b, idx) => (
                        <option
                          key={idx}
                          value={String(
                            b.id ??
                              b.fld_Branchid ??
                              b.branchId ??
                              b.name ??
                              "Select"
                          )}
                        >
                          {String(
                            b.name ??
                              b.fld_BranchName ??
                              b.branchName ??
                              b.id ??
                              b.fld_Branchid ??
                              "Select"
                          )}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Store */}
                  <div>
                    <label className={labelClass}>Store</label>
                    <select
                      name="storeId"
                      value={filters.storeId}
                      onChange={onFilterChange}
                      className={inputClass}
                      disabled={isBusy}
                    >
                      <option value="Select">Select</option>
                      {stores.map((s, idx) => (
                        <option
                          key={idx}
                          value={String(
                            s.id ?? s.fld_storeid ?? s.storeId ?? s.name ?? "Select"
                          )}
                        >
                          {String(
                            s.name ??
                              s.fld_storename ??
                              s.storeName ??
                              s.id ??
                              s.fld_storeid ??
                              "Select"
                          )}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Vehicle Type */}
                  <div>
                    <label className={labelClass}>Vehicle Type</label>
                    <select
                      name="vehicleTypeIndex"
                      value={filters.vehicleTypeIndex}
                      onChange={onFilterChange}
                      className={inputClass}
                      disabled={isBusy}
                    >
                      <option value="Select">Select</option>
                      {vehicleTypes.map((t, idx) => (
                        <option
                          key={idx}
                          value={String(
                            t.id ??
                              t.fld_vehicletypeindex ??
                              t.vehicleTypeIndex ??
                              t.name ??
                              "Select"
                          )}
                        >
                          {String(
                            t.name ??
                              t.fld_vehicletypename ??
                              t.vehicleTypeName ??
                              t.id ??
                              t.fld_vehicletypeindex ??
                              "Select"
                          )}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Vehicle Reg No */}
                  <div>
                    <label className={labelClass}>Vehicle Registration Number</label>
                    <input
                      type="text"
                      name="vehicleRegNo"
                      value={filters.vehicleRegNo}
                      onChange={onFilterChange}
                      className={inputClass}
                      placeholder="Enter vehicle registration number"
                      disabled={filters.filterType === "spares" || isBusy}
                    />
                  </div>

                  {/* Vehicle Code */}
                  <div>
                    <label className={labelClass}>Vehicle Code</label>
                    <input
                      type="text"
                      name="vehicleCode"
                      value={filters.vehicleCode}
                      onChange={onFilterChange}
                      className={inputClass}
                      placeholder="Enter vehicle code"
                      disabled={isBusy}
                    />
                  </div>

                  {/* Vehicle Name */}
                  <div>
                    <label className={labelClass}>Vehicle Name</label>
                    <input
                      type="text"
                      name="vehicleName"
                      value={filters.vehicleName}
                      onChange={onFilterChange}
                      className={inputClass}
                      placeholder="Enter vehicle name"
                      disabled={filters.filterType === "spares" || isBusy}
                    />
                  </div>
                </div>

                {/* action buttons */}
                <div className="flex flex-col md:flex-row gap-3 justify-between items-center mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                  <button
                    onClick={handleClearFilters}
                    disabled={isBusy}
                    className={`flex items-center gap-2 ${
                      isDark ? "text-gray-300 hover:text-white" : "text-gray-600 hover:text-gray-900"
                    } font-medium transition-colors duration-200 ${
                      isBusy ? "opacity-60 cursor-not-allowed" : ""
                    }`}
                  >
                    <FaUndo />
                    Clear Filters
                  </button>

                  <div className="flex gap-3">
                    <button
                      onClick={handleSearch}
                      disabled={isBusy}
                      className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-60`}
                    >
                      <FaSearch />
                      {gridLoading ? "Searching..." : "Search"}
                    </button>

                    <button
                      onClick={handleViewAll}
                      disabled={isBusy}
                      className="flex items-center gap-2 px-6 py-2 border border-gray-300 dark:border-gray-600 rounded-lg font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-60"
                    >
                      <FaEye />
                      View All
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* GRID */}
          <div className="mt-8">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-3">
              <div className={`text-lg font-semibold ${isDark ? "text-white" : "text-gray-800"}`}>
                Vehicle Transfer Entry List
              </div>
              <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                Vehicle Mode:{" "}
                <span className="font-semibold">
                  {filters.filterType === "own" ? "Own Vehicle" : "Spares & Catalogue"}
                </span>
                {" • "}
                Records: <span className="font-semibold">{rows.length}</span>
              </div>
            </div>

            <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
              <table className="min-w-full text-sm">
                <thead className={`${buttonClass} text-white`}>
                  <tr>
                    <th className="px-4 py-3 text-center">S.No</th>
                    <th className="px-4 py-3 text-center">Vehicle Main Type</th>
                    <th className="px-4 py-3 text-center">Vehicle Code</th>
                    <th className="px-4 py-3 text-center">Vehicle Name</th>
                    <th className="px-4 py-3 text-center">Vehicle Registration Number</th>
                    <th className="px-4 py-3 text-center">Changing Branch</th>
                  </tr>
                </thead>

                <tbody className={isDark ? "bg-gray-900 text-gray-100" : "bg-white text-gray-800"}>
                  {rows.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-10 text-center text-gray-500">
                        {gridLoading || pageLoading ? "Loading..." : "No records found"}
                      </td>
                    </tr>
                  ) : (
                    rows.map((row, idx) => (
                      <tr
                        key={row?.id ?? row?.vehicleId ?? idx}
                        className={isDark ? "border-t border-gray-800" : "border-t border-gray-100"}
                      >
                        <td className="px-4 py-3 text-center whitespace-nowrap">
                          {idx + 1}
                        </td>
                        <td className="px-4 py-3 text-center whitespace-nowrap">
                          {row?.mainType ||
                            row?.vehicleMainType ||
                            row?.fld_vehicletypename ||
                            "-"}
                        </td>
                        <td className="px-4 py-3 text-center whitespace-nowrap">
                          {row?.code || row?.vehicleCode || row?.fld_vehiclecode || "-"}
                        </td>
                        <td className="px-4 py-3 text-center whitespace-nowrap">
                          {row?.name || row?.vehicleName || row?.fld_vehiclename || "-"}
                        </td>
                        <td className="px-4 py-3 text-center whitespace-nowrap">
                          {row?.regNo ||
                            row?.vehicleRegNo ||
                            row?.fld_registrationno ||
                            "-"}
                        </td>
                        <td className="px-4 py-3 text-center whitespace-nowrap">
                          <button
                            onClick={() => handleChangeBranch(row)}
                            disabled={isBusy}
                            className="inline-flex items-center gap-2 px-3 py-2 bg-blue-600 text-white text-xs font-semibold rounded-lg hover:bg-blue-700 transform hover:-translate-y-0.5 transition-all duration-200 shadow-md hover:shadow-lg disabled:opacity-60"
                          >
                            <FaExchangeAlt className="w-3 h-3" />
                            Change
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {isBusy && (
              <div className={`mt-3 text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                {pageLoading
                  ? "Loading page..."
                  : dropdownLoading
                  ? "Loading dropdown..."
                  : gridLoading
                  ? "Loading grid..."
                  : "Loading..."}
              </div>
            )}
          </div>

          {/* Back */}
          <div className="flex justify-start mt-6">
            <button
              onClick={handleBack}
              disabled={isBusy}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                isDark ? "bg-gray-700 hover:bg-gray-600 text-white" : "bg-gray-200 hover:bg-gray-300 text-gray-700"
              } ${isBusy ? "opacity-70 cursor-not-allowed" : ""}`}
            >
              <FaArrowLeft />
              Back
            </button>
          </div>

          <ToastContainer
            position="top-right"
            autoClose={2500}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

export default VehicleTransferEntry;